// KAN-167: presentation helpers for Travel eSIM bundles. Pure functions, no React.
import type { EsimBundle, EsimBalanceType, RoamingProfile } from '../services/adminService';

export const BALANCE_TYPE_ORDER: EsimBalanceType['key'][] = [
  'standard',
  'throttled-1mbps',
  'throttled-5mbps',
  'throttled',
  'unknown',
];

// Short, human explanation used next to a bundle wherever it is chosen or shown.
export function balanceTypeSummary(t: EsimBalanceType | null | undefined): string {
  switch (t?.key) {
    case 'standard':
      return 'Stops when the allowance is used up';
    case 'throttled-1mbps':
      return 'Slows to 1 Mbps after the allowance';
    case 'throttled-5mbps':
      return 'Slows to 5 Mbps after the allowance';
    case 'throttled':
      return 'Slows down after the allowance';
    default:
      return 'Behaviour after the allowance unknown';
  }
}

export function isThrottled(t: EsimBalanceType | null | undefined): boolean {
  return !!t && t.key.startsWith('throttled');
}

export function formatValidity(days: number | null | undefined): string {
  if (!days || days <= 0) return 'validity unknown';
  if (days === 1) return '1 day';
  if (days % 365 === 0) return days === 365 ? '1 year' : `${days / 365} years`;
  if (days % 30 === 0 && days >= 60) return `${days / 30} months`;
  return `${days} days`;
}

export function formatMb(mb: number | null | undefined): string {
  if (mb === null || mb === undefined || !Number.isFinite(mb)) return '—';
  if (mb >= 1048576) return `${trim(mb / 1048576)} TB`;
  if (mb >= 1024) return `${trim(mb / 1024)} GB`;
  return `${trim(mb)} MB`;
}

function trim(n: number): string {
  return Number.isInteger(n) ? String(n) : n.toFixed(1).replace(/\.0$/, '');
}

// "Travel SIM 5GB-30D · 5 GB · 30 days"
export function bundleLabel(b: EsimBundle): string {
  const parts = [b.name];
  if (b.data) parts.push(b.data);
  if (b.validity) parts.push(formatValidity(b.validity));
  return parts.join(' · ');
}

export interface BundleGroup {
  key: EsimBalanceType['key'];
  label: string;
  description: string;
  bundles: EsimBundle[];
}

// Group a family's bundles by balance type, largest allowance last within a
// group, so the select reads Standard → Throttled 1 Mbps → Throttled 5 Mbps.
export function groupBundlesByBalanceType(bundles: EsimBundle[]): BundleGroup[] {
  const map = new Map<EsimBalanceType['key'], BundleGroup>();
  for (const b of bundles) {
    if (b.retired) continue;
    const key = b.balanceType?.key ?? 'unknown';
    if (!map.has(key)) {
      map.set(key, {
        key,
        label: b.balanceType?.label ?? 'Unknown',
        description: b.balanceType?.description ?? '',
        bundles: [],
      });
    }
    map.get(key)!.bundles.push(b);
  }
  const groups = [...map.values()];
  for (const g of groups) {
    g.bundles.sort(
      (a, c) => (a.valueMb ?? 0) - (c.valueMb ?? 0) || (a.validity ?? 0) - (c.validity ?? 0)
    );
  }
  groups.sort((a, c) => BALANCE_TYPE_ORDER.indexOf(a.key) - BALANCE_TYPE_ORDER.indexOf(c.key));
  return groups;
}

export function splitByFamily(bundles: EsimBundle[]): {
  travelSim: EsimBundle[];
  travelZone: EsimBundle[];
} {
  return {
    travelSim: bundles.filter(b => b.family === 'travel-sim'),
    travelZone: bundles.filter(b => b.family === 'travel-zone'),
  };
}

// "Europe – tier 3 (zones 1–3, 41 countries)" / "Single country – Spain"
export function roamingLabel(p: RoamingProfile): string {
  if (p.kind === 'single-country') {
    const country = (p.name || '').replace(/^Roaming control\s+/i, '').replace(/\s+-\s+\d+$/, '');
    return `Single country – ${country || p.region || p.id}`;
  }
  if (p.kind === 'legacy-zone') {
    return `${(p.name || '').replace(/^Roaming control\s+/i, '') || `Zone profile ${p.id}`} (older fixed package)`;
  }
  const tier = (p.zoneMapping || '').split('+').length;
  const count =
    p.countryCount != null
      ? ` · ${p.countryCount} ${p.countryCount === 1 ? 'country' : 'countries'}`
      : '';
  return `${p.region || p.group} – tier ${tier}${count}`;
}

export interface RoamingGroup {
  label: string;
  options: RoamingProfile[];
}

// Regional tiers first (by region), then single countries alphabetically.
export function groupRoamingProfiles(
  profiles: RoamingProfile[],
  includeLegacy = false
): RoamingGroup[] {
  const regional = new Map<string, RoamingProfile[]>();
  const single: RoamingProfile[] = [];
  for (const p of profiles) {
    if (p.kind === 'legacy-zone' && !includeLegacy) continue;
    if (p.kind === 'single-country') single.push(p);
    else {
      const key = p.kind === 'legacy-zone' ? 'Legacy Travel Zone profiles' : p.region || p.group;
      if (!regional.has(key)) regional.set(key, []);
      regional.get(key)!.push(p);
    }
  }
  const groups: RoamingGroup[] = [...regional.entries()]
    .sort(([a], [b]) => (a === 'Global' ? -1 : b === 'Global' ? 1 : a.localeCompare(b)))
    .map(([label, options]) => ({ label, options: options.sort((a, b) => a.id - b.id) }));
  if (single.length) {
    groups.push({
      label: 'Single country',
      options: single.sort((a, b) => roamingLabel(a).localeCompare(roamingLabel(b))),
    });
  }
  return groups;
}

export function roamingKey(p: { group: string; id: number }): string {
  return `${p.group}::${p.id}`;
}

export function parseRoamingKey(key: string): { group: string; id: number } | null {
  const idx = key.lastIndexOf('::');
  if (idx < 0) return null;
  const id = Number(key.slice(idx + 2));
  return Number.isFinite(id) ? { group: key.slice(0, idx), id } : null;
}

// Percentage of a bundle left, for the progress bar. null when unknown.
export function remainingPercent(
  remainingMb: number | null,
  totalMb: number | null
): number | null {
  if (remainingMb == null || totalMb == null || totalMb <= 0) return null;
  return Math.max(0, Math.min(100, Math.round((remainingMb / totalMb) * 100)));
}

export function timeAgo(iso: string | null | undefined): string {
  if (!iso) return 'never';
  const secs = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 1000));
  if (secs < 60) return `${secs}s ago`;
  if (secs < 3600) return `${Math.round(secs / 60)} min ago`;
  if (secs < 86400) return `${Math.round(secs / 3600)} h ago`;
  return `${Math.round(secs / 86400)} d ago`;
}
